import { Injectable } from "@angular/core";
import {
  BfxBaseBlotterActionService,
  ActionListener,
} from "app/bfx-base-blotter/bfx-base-blotter-action.service";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import {
  GoldenLayoutService,
  GLPopoutConfiguration,
  GLPopoutState,
  GLPopoutUndockBehaviour,
} from "app/golden-layout/golden-layout-service";
import { TranslateService } from "@ngx-translate/core";
import { BfxConfigService } from "shared/auth-services/system-config/bfx-config.service";
import {
  BmarkHedgeOrdersContextMenuService,
  BmarkHedgeOrdersContextMenuItemState,
} from "./bmark-hedge-orders-context-menu.service";
import { DashboardItemType } from "app/bfx-common/models/dashboard-item-type.enum";
import { GoldenLayoutAdditionMode } from "app/golden-layout/golden-layout-addition-mode";
import { BfxBlotterActionEvent } from "app/bfx-base-blotter/bfx-base-blotter-contextmenu.service";
import { OENEntryType } from "app/order-entry/data/enums/oe-entry-type.enum";
import { OENOperationMode } from "app/order-entry/data/enums/oe-operation-mode.enum";
import { OEUtilityService } from "app/order-entry/services/basic/oe-utility.service";
import { OESettingsService } from "app/order-entry/services/basic/oe-settings.service";
import {
  BfxBlotterAction,
  OrderDataActionPromiseService,
  ActiveUser,
  HedgeOrderDataLookupService,
  OrderAcceptancePromiseService,
  TraderGroupLookupService,
  OrderPauseState,
  BfxBlotterOrdersContextMenuItemUnlockState,
} from "shared/cubiq-services";
import { OEApiService } from "app/order-entry/services/oe-api.service";
import { BfxOrderCancelComponentLauncher } from "app/bfx-order-cancel/bfx-order-cancel.launcher";
import {
  AcceptanceAction,
  AcceptanceType,
  OrderType,
  OrderDataImpl,
  OrderState,
  Long,
  TradingGroupImpl,
  OrderIntent,
  PauseState,
  LockType,
  BenchmarkOrderType,
  InflightInclusionState,
} from "shared/cubiq-model-multi-asset";
import { Subject } from "rxjs";
import { BfxDialogData } from "app/bfx-dialogs/models/bfx-dialog-data.model";
import { BfxDialogCommand } from "app/bfx-dialogs/models/bfx-dialog-command.enum";
import { NativeDesktopSupport } from "app/bfx-native-desktop/native-desktop-support";
import {
  BfxOrderPauseResumeComponentLauncher,
  OrderPauseResumeInstruction,
} from "app/bfx-order-pauseresume/bfx-order-pauseresume.launcher";
import { OrderStateRenderer } from "shared/cubiq-services/model/orders/OrderStateRenderer";
import { BfxConfigIDRefHelper } from "../../../../../shared/helper-services/bfx-config-id-ref-helper";
import { NativeDesktopWindow } from "shared/native-desktop/NativeDesktopWindow";
import { BfxBlotterOrdersActionSubject } from "app/bfx-blotter-orders/bfx-blotter-orders-action-subject.module";
import { DETAILS_MIN_SIZE } from "shared/cubiq-services/model/golden-layout.constants";

@Injectable()
export class BfxBmarkHedgeOrdersBlotterActionsService extends BfxBaseBlotterActionService<any> {
  /**********************************************************************/
  /* OBSERVABLE */
  // Observable string sources
  private orderActionDialogSource = new Subject<BfxDialogData>();
  // Observable string streams
  public dialogCtrl$ = this.orderActionDialogSource.asObservable();
  private orderStateRenderer: OrderStateRenderer;
  statePaused: string;
  constructor(
    protected contextMenuService: BmarkHedgeOrdersContextMenuService,
    protected layoutService: GoldenLayoutService,
    protected translationService: TranslateService,
    protected configService: BfxConfigService,
    protected oeUtility: OEUtilityService,
    protected settings: OESettingsService,
    protected oeDataService: OrderDataActionPromiseService,
    private oeApi: OEApiService,
    private hedgeOrdersDataService: HedgeOrderDataLookupService,
    private orderAcceptanceService: OrderAcceptancePromiseService,
    protected myTraderGroupLookup: TraderGroupLookupService,
    protected orderActionService: OrderDataActionPromiseService,
    private actionSubject: BfxBlotterOrdersActionSubject,
  ) {
    super(contextMenuService, layoutService, translationService, configService);
    this.orderStateRenderer = new OrderStateRenderer(this.translationService);
    this.statePaused = this.translationService.instant("order.state.PAUSED");
  }

  @ActionListener(BfxBlotterAction.ACTION_ORDER_ACCEPT_CANCEL)
  public onOrderAccept(event: BfxBlotterActionEvent) {
    let selectedPositionIds: string[] = [];
    let selectedCancelPositionIds: string[] = [];
    let selectedEntryAlgoIds: string[] = [];
    let selectedCancelAlgoIds: string[] = [];
    let selectedPauseAlgoIds: string[] = [];
    let selectedResumeAlgoIds: string[] = [];

    let comment = "";
    let hasExternal = (event.itemState as BmarkHedgeOrdersContextMenuItemState).hasExternal;

    // Position orders need to be separated from algos
    if (event.selectedItems != null && event.selectedItems.length > 0) {
      event.selectedItems.forEach((item: OrderDataImpl) => {
        if (item.orderType == OrderType.BENCHMARK) {
          if (
            item.orderState == OrderState.ACCEPTANCE_NEEDED ||
            item.orderState == OrderState.EXT_ACCEPTANCE_NEEDED
          ) {
            selectedPositionIds.push(item.orderId.toString());
          } else if (
            item.orderState == OrderState.CANCEL_ACCEPTANCE_NEEDED ||
            item.orderState == OrderState.EXT_CANCEL_ACCEPTANCE_NEEDED
          ) {
            selectedCancelPositionIds.push(item.orderId.toString());
          }
        } else if (item.orderType == OrderType.BMALGO) {
          if (OrderPauseState.isPausePendingOrderState(item)) {
            selectedPauseAlgoIds.push(item.orderId.toString());
          } else if (OrderPauseState.isResumePendingOrderState(item)) {
            selectedResumeAlgoIds.push(item.orderId.toString());
          } else if (
            item.orderState == OrderState.ACCEPTANCE_NEEDED ||
            item.orderState == OrderState.EXT_ACCEPTANCE_NEEDED
          ) {
            selectedEntryAlgoIds.push(item.orderId.toString());
          } else if (
            item.orderState == OrderState.CANCEL_ACCEPTANCE_NEEDED ||
            item.orderState == OrderState.EXT_CANCEL_ACCEPTANCE_NEEDED
          ) {
            selectedCancelAlgoIds.push(item.orderId.toString());
          }
        }
      });
    } else {
      selectedPositionIds = event.selectedIds;
    }

    let acceptFn = () => {
      if (selectedPositionIds.length > 0) {
        this.orderAcceptanceService.requestAcceptance(
          selectedPositionIds,
          comment,
          AcceptanceAction.ACCEPT,
          AcceptanceType.ENTRY,
          null,
          null,
          OrderType.BENCHMARK,
          BenchmarkOrderType.PositionOrder,
        );
      }

      if (selectedCancelPositionIds.length > 0) {
        this.orderAcceptanceService.requestAcceptance(
          selectedCancelPositionIds,
          comment,
          AcceptanceAction.ACCEPT,
          AcceptanceType.CANCEL,
          null,
          null,
          OrderType.BENCHMARK,
          BenchmarkOrderType.PositionOrder,
        );
      }

      if (selectedEntryAlgoIds.length > 0) {
        this.orderAcceptanceService.requestAcceptance(
          selectedEntryAlgoIds,
          comment,
          AcceptanceAction.ACCEPT,
          AcceptanceType.ENTRY,
          null,
          null,
          OrderType.BMALGO,
        );
      }

      if (selectedCancelAlgoIds.length > 0) {
        this.orderAcceptanceService.requestAcceptance(
          selectedCancelAlgoIds,
          comment,
          AcceptanceAction.ACCEPT,
          AcceptanceType.CANCEL,
          null,
          null,
          OrderType.BMALGO,
        );
      }

      if (selectedPauseAlgoIds.length > 0) {
        this.orderAcceptanceService.requestAcceptance(
          selectedPauseAlgoIds,
          comment,
          AcceptanceAction.ACCEPT,
          AcceptanceType.PAUSE,
          null,
          null,
          OrderType.BMALGO,
        );
      }

      if (selectedResumeAlgoIds.length > 0) {
        this.orderAcceptanceService.requestAcceptance(
          selectedResumeAlgoIds,
          comment,
          AcceptanceAction.ACCEPT,
          AcceptanceType.RESUME,
          null,
          null,
          OrderType.BMALGO,
        );
      }
    };

    if (hasExternal) {
      let msg =
        event.selectedIds.length == 1
          ? "force.accept.order.external.single"
          : "force.accept.order.external.multi";
      let payload = {
        messages: [this.translationService.instant(msg)],
        title: "force.accept.order.title",
        callback: acceptFn,
      };

      let dialogData: BfxDialogData = new BfxDialogData(
        BfxDialogCommand.DIALOG_GENERIC_CONFIRM_OPEN,
        null,
        null,
        payload,
      );

      this.setDialogBody(dialogData).then(() => {
        this.orderActionDialogSource.next(dialogData);
      });
    } else {
      acceptFn();
    }
  }

  @ActionListener(BfxBlotterAction.ACTION_ORDER_REJECT_CANCEL)
  public onOrderReject(event: BfxBlotterActionEvent) {
    let selectedPositionIds: string[] = [];
    let selectedCancelPositionIds: string[] = [];
    let selectedEntryAlgoIds: string[] = [];
    let selectedCancelAlgoIds: string[] = [];
    let selectedPauseAlgoIds: string[] = [];
    let selectedResumeAlgoIds: string[] = [];

    let comment = "";
    let hasExternal = (event.itemState as BmarkHedgeOrdersContextMenuItemState).hasExternal;

    // Position orders need to be separated from algos
    if (event.selectedItems != null && event.selectedItems.length > 0) {
      event.selectedItems.forEach((item: OrderDataImpl) => {
        if (item.orderType == OrderType.BENCHMARK) {
          if (
            item.orderState == OrderState.ACCEPTANCE_NEEDED ||
            item.orderState == OrderState.EXT_ACCEPTANCE_NEEDED
          ) {
            selectedPositionIds.push(item.orderId.toString());
          } else if (
            item.orderState == OrderState.CANCEL_ACCEPTANCE_NEEDED ||
            item.orderState == OrderState.EXT_CANCEL_ACCEPTANCE_NEEDED
          ) {
            selectedCancelPositionIds.push(item.orderId.toString());
          }
        } else if (item.orderType == OrderType.BMALGO) {
          if (OrderPauseState.isPausePendingOrderState(item)) {
            selectedPauseAlgoIds.push(item.orderId.toString());
          } else if (OrderPauseState.isResumePendingOrderState(item)) {
            selectedResumeAlgoIds.push(item.orderId.toString());
          } else if (
            item.orderState == OrderState.ACCEPTANCE_NEEDED ||
            item.orderState == OrderState.EXT_ACCEPTANCE_NEEDED
          ) {
            selectedEntryAlgoIds.push(item.orderId.toString());
          } else if (
            item.orderState == OrderState.CANCEL_ACCEPTANCE_NEEDED ||
            item.orderState == OrderState.EXT_CANCEL_ACCEPTANCE_NEEDED
          ) {
            selectedCancelAlgoIds.push(item.orderId.toString());
          }
        }
      });
    } else {
      selectedPositionIds = event.selectedIds;
    }

    let rejectFn = () => {
      if (selectedPositionIds.length > 0) {
        this.orderAcceptanceService.requestAcceptance(
          selectedPositionIds,
          comment,
          AcceptanceAction.REJECT,
          AcceptanceType.ENTRY,
          null,
          null,
          OrderType.BENCHMARK,
          BenchmarkOrderType.PositionOrder,
        );
      }

      if (selectedCancelPositionIds.length > 0) {
        this.orderAcceptanceService.requestAcceptance(
          selectedCancelPositionIds,
          comment,
          AcceptanceAction.REJECT,
          AcceptanceType.CANCEL,
          null,
          null,
          OrderType.BENCHMARK,
          BenchmarkOrderType.PositionOrder,
        );
      }

      if (selectedEntryAlgoIds.length > 0) {
        this.orderAcceptanceService.requestAcceptance(
          selectedEntryAlgoIds,
          comment,
          AcceptanceAction.REJECT,
          AcceptanceType.ENTRY,
          null,
          null,
          OrderType.BMALGO,
        );
      }

      if (selectedCancelAlgoIds.length > 0) {
        this.orderAcceptanceService.requestAcceptance(
          selectedCancelAlgoIds,
          comment,
          AcceptanceAction.REJECT,
          AcceptanceType.CANCEL,
          null,
          null,
          OrderType.BMALGO,
        );
      }

      if (selectedPauseAlgoIds.length > 0) {
        this.orderAcceptanceService.requestAcceptance(
          selectedPauseAlgoIds,
          comment,
          AcceptanceAction.REJECT,
          AcceptanceType.PAUSE,
          null,
          null,
          OrderType.BMALGO,
        );
      }

      if (selectedResumeAlgoIds.length > 0) {
        this.orderAcceptanceService.requestAcceptance(
          selectedResumeAlgoIds,
          comment,
          AcceptanceAction.REJECT,
          AcceptanceType.RESUME,
          null,
          null,
          OrderType.BMALGO,
        );
      }
    };

    if (hasExternal) {
      let msg =
        event.selectedIds.length == 1
          ? "force.reject.order.external.single"
          : "force.reject.order.external.multi";
      let payload = {
        messages: [this.translationService.instant(msg)],
        title: "force.reject.order.title",
        callback: rejectFn,
      };

      let dialogData: BfxDialogData = new BfxDialogData(
        BfxDialogCommand.DIALOG_GENERIC_CONFIRM_OPEN,
        null,
        null,
        payload,
      );

      this.setDialogBody(dialogData).then(() => {
        this.orderActionDialogSource.next(dialogData);
      });
    } else {
      rejectFn();
    }
  }

  private async setDialogBody(dialogData: BfxDialogData): Promise<void> {
    if (NativeDesktopSupport.isNativeDesktopContainer()) {
      let wnd: NativeDesktopWindow = await NativeDesktopSupport.findActive(true);
      if (wnd != null) {
        dialogData.body = wnd.getNativeWindow().document.body;
      }
    }

    return Promise.resolve();
  }

  @ActionListener(BfxBlotterAction.ACTION_ORDER_PAUSE)
  public onPause(event: BfxBlotterActionEvent): void {
    // TODO not sure what these do/did? (unref in BfxOrderCancelComponent -dc)
    event["breach"] = false;
    event["transferItems"] = [];

    let instructions: Array<OrderPauseResumeInstruction> = this.buildPauseResumeInstructions(
      event.selectedItems,
    );

    BfxOrderPauseResumeComponentLauncher.launch(
      BfxBlotterAction.ACTION_ORDER_PAUSE,
      instructions,
      this.layoutService,
      this.translationService,
    );
  }

  @ActionListener(BfxBlotterAction.ACTION_ORDER_RESUME)
  public onResume(event: BfxBlotterActionEvent): void {
    // TODO not sure what these do/did? (unref in BfxOrderCancelComponent -dc)
    event["breach"] = false;
    event["transferItems"] = [];

    let instructions: Array<OrderPauseResumeInstruction> = this.buildPauseResumeInstructions(
      event.selectedItems,
    );

    BfxOrderPauseResumeComponentLauncher.launch(
      BfxBlotterAction.ACTION_ORDER_RESUME,
      instructions,
      this.layoutService,
      this.translationService,
    );
  }
  /* Fill Now */
  @ActionListener(BfxBlotterAction.ACTION_ORDER_FILL_NOW)
  public onFillNow(event: BfxBlotterActionEvent): void {
    // Forward on the fill Now event to the blotter service
    this.actionSubject.next(event);
  }

  @ActionListener(BfxBlotterAction.ACTION_ORDER_ENABLE_INFLIGHT)
  public onInflightCheckEnable(event: BfxBlotterActionEvent): void {
    let orderIds: Long[] = event.selectedItems
      .filter(
        (order: OrderDataImpl) =>
          order.orderType === OrderType.BMALGO &&
          order.inflightInclusionState === InflightInclusionState.DISABLED,
      )
      .map((o) => o.orderId);
    this.orderActionService.toggleInflightInclusionState(orderIds, InflightInclusionState.ENABLED);
  }

  @ActionListener(BfxBlotterAction.ACTION_ORDER_DISABLE_INFLIGHT)
  public onInflightCheckDisable(event: BfxBlotterActionEvent): void {
    let orderIds: Long[] = event.selectedItems
      .filter(
        (order: OrderDataImpl) =>
          order.orderType === OrderType.BMALGO &&
          order.inflightInclusionState === InflightInclusionState.ENABLED,
      )
      .map((o) => o.orderId);
    this.orderActionService.toggleInflightInclusionState(orderIds, InflightInclusionState.DISABLED);
  }

  /* ORDER VIEW DETAILS */
  @ActionListener(BfxBlotterAction.ACTION_VIEW_DETAILS)
  public onOrderDetails(event: BfxBlotterActionEvent) {
    let config: GLPopoutConfiguration = new GLPopoutConfiguration();
    config.initialState = GLPopoutState.POPOUT;
    config.undockBehaviour = GLPopoutUndockBehaviour.APPLY_BOUNDING_SIZE;
    config.popoutResizable = false;
    config.sourceEvent = event.sourceEvent;
    config.ladderBlotter = event.ladderBlotter;
    config.minimumSize = DETAILS_MIN_SIZE;

    config.data = {
      selectedIds: event.selectedIds,
      allocationId: event.selectedItems[0].allocationId,
      openTradesTab: false,
    };

    let title: string = this.translationService.instant("order.details.title", [
      BfxConfigIDRefHelper.getOrderRef(event.selectedItems[0]),
    ]);

    this.layoutService.launchFromDashboard(
      title,
      DashboardItemType.OrderDetails,
      true,
      GoldenLayoutAdditionMode.DEFAULT,
      config,
    );
  }

  @ActionListener(BfxBlotterAction.ACTION_ORDER_MODIFY)
  public onOrderModify(event: BfxBlotterActionEvent) {
    this.oeApi.doOrderEntryPopupForOrderId(event.selectedIds[0], OENOperationMode.MODIFY);
  }

  @ActionListener(BfxBlotterAction.ACTION_VIEW_AUDIT)
  public onViewAudit(event: BfxBlotterActionEvent) {
    let selectedId: any = event.selectedIds[0];
    let itemType: DashboardItemType = DashboardItemType.OrderBlotterAudit;
    let config: GLPopoutConfiguration = new GLPopoutConfiguration();
    config.popoutResizable = true;
    config.preferredSize = {
      width: Math.max(750, window.innerWidth / 2),
      height: 745,
    };
    config.minimumSize = { width: 630, height: 345 };

    config.initialState = GLPopoutState.POPOUT;
    config.undockBehaviour = GLPopoutUndockBehaviour.APPLY_BOUNDING_SIZE;
    config.sourceEvent = event.sourceEvent;
    config.data = {
      orderId: selectedId,
    };

    let titleId: string = selectedId;
    if (event.selectedItems != null && event.selectedItems.length > 0) {
      config.data.isBenchmarkAlgoOrder = event.selectedItems[0].orderType === OrderType.BMALGO;
      config.data.isBenchmarkOrder = event.selectedItems[0].orderType === OrderType.BENCHMARK;
    }

    if (
      event.selectedItems != null &&
      event.selectedItems.length > 0 &&
      event.selectedItems[0].orderRef != null
    ) {
      titleId = event.selectedItems[0].orderRef;
    }

    let title: string = this.translationService.instant("order.audit.title", [titleId]);

    this.layoutService.launchFromDashboard(
      title,
      itemType,
      true,
      GoldenLayoutAdditionMode.DEFAULT,
      config,
    );
  }

  @ActionListener(BfxBlotterAction.ACTION_ORDER_UNLOCK)
  public onOrderUnlock(event: BfxBlotterActionEvent) {
    // Forward on the Unlock event to the blotter service

    if (event.selectedItems?.length == 1) {
      (event.itemState as BfxBlotterOrdersContextMenuItemUnlockState).lockType =
        event.selectedItems[0]?.lockType;
    }
    this.actionSubject.next(event);
  }

  @ActionListener(BfxBlotterAction.ACTION_ORDER_CANCEL)
  public onCancel(event: BfxBlotterActionEvent): void {
    BfxOrderCancelComponentLauncher.launch(
      event.selectedItems,
      this.layoutService,
      this.translationService,
      event.sourceEvent,
    );
  }

  /* EXPORT */
  @ActionListener(BfxBlotterAction.ACTION_TRADE_EXPORT)
  public onExport(event: BfxBlotterActionEvent) {
    event.selectedIds = [];
    event.selectedItems.forEach((order) => event.selectedIds.push(order.primaryKey));
    this.orderActionDialogSource.next(
      new BfxDialogData(BfxDialogCommand.DIALOG_CSV_EXPORT_OPEN, null, event, {
        blotterName: "HedgeOrdersBlotter",
      }),
    );
  }

  buildPauseResumeInstructions(candidates: Array<any>): Array<OrderPauseResumeInstruction> {
    let instructions: Array<OrderPauseResumeInstruction> = new Array<OrderPauseResumeInstruction>();

    candidates.map((order) => {
      let instruction: OrderPauseResumeInstruction = new OrderPauseResumeInstruction();
      instruction.order = order;

      instructions.push(instruction);
    });

    return instructions;
  }
}
